import { gql } from '@apollo/client';
import React, { useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useParams, Link } from 'react-router-dom';
import { TrunkshowCellFragment } from '../../../../generated/types/TrunkshowCellFragment';
import { hrefFor } from '../../../../routers';
import { compact } from '../../../../lib/compact';
import {
  useHasAccessToProductOrTrunkshow,
  HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT
} from '../../../../hooks/useHasAccessToProductOrTrunkshow';
import { ImageGallery } from '../../../ImageGallery';
import { RelativeTime } from '../../../RelativeTime';
import { useGridCellImageSize } from '../useGridCellImageSize';
import './TrunkshowCell.scss';

export const TRUNKSHOW_CELL_FRAGMENT = gql`
  fragment TrunkshowCellFragment on Trunkshow {
    id
    slug
    name
    activeEndAt: active_end_i
    primaryImageUrls: primary_image_urls {
      src: main
    }
    previewImageUrls: preview_image_urls
    ...HasAccessToTrunkshowFragment
  }
  ${HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT}
`;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  trunkshow: TrunkshowCellFragment;
}

export const TrunkshowCell: React.FC<Props> = ({ className, trunkshow }) => {
  const { vertical = 'women' } = useParams<{ vertical?: string }>();
  const [hovered, setHovered] = useState(false);

  const { maxWidth, maxHeight } = useGridCellImageSize();

  const { hasAccess } = useHasAccessToProductOrTrunkshow(trunkshow);

  const href = hrefFor.TrunkshowPage({
    vertical: vertical,
    trunkshowId: trunkshow.id,
    trunkshowSlug: trunkshow.slug || trunkshow.id
  });

  const imageSrcs = useMemo(
    () => compact([trunkshow.primaryImageUrls?.src, ...trunkshow.previewImageUrls]),
    [trunkshow]
  );

  const images = useMemo(() => imageSrcs.map(src => ({ src, title: trunkshow.name })), [
    imageSrcs,
    trunkshow
  ]);

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  if (!hasAccess) return null;

  return (
    <Link
      className={classNames('TrunkshowCell', className)}
      to={href || ''}
      data-pk={trunkshow.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageGallery images={images} active={hovered} maxHeight={maxHeight} maxWidth={maxWidth} />

      {trunkshow.activeEndAt && trunkshow.name && (
        <div className="TrunkshowCell__overlay">
          <RelativeTime className="TrunkshowCell__timestamp" timestamp={trunkshow.activeEndAt}>
            Left
          </RelativeTime>

          <h3 className="TrunkshowCell__title">{trunkshow.name}</h3>
        </div>
      )}
    </Link>
  );
};
